import React, { useState, useEffect } from "react";
import "./reports.css";

const API = "/php";

const MONTHS = [
    ["1", "Январь"], ["2", "Февраль"], ["3", "Март"], ["4", "Апрель"],
    ["5", "Май"], ["6", "Июнь"], ["7", "Июль"], ["8", "Август"],
    ["9", "Сентябрь"], ["10", "Октябрь"], ["11", "Ноябрь"], ["12", "Декабрь"]
];

const Reports = ({ setChecked }) => {
    useEffect(() => { setChecked("5"); }, []);

    const currentMonth = new Date().getMonth() + 1;
    const [checkedMonth, setCheckedMonth] = useState(`${currentMonth}`);
    const [activeTab, setActiveTab] = useState("abonent"); // abonent | money | gps | zayavki
    const [loading, setLoading] = useState(false);

    // Данные
    const [abonentData, setAbonentData] = useState([]);
    const [moneyData, setMoneyData] = useState([]);
    const [gpsData, setGpsData] = useState([]);
    const [zayavkiData, setZayavkiData] = useState([]);

    // =============================================
    // Загрузка данных при смене месяца или вкладки
    // =============================================
    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const [r1, r2, r3, r4] = await Promise.all([
                    fetch(`${API}/get_abonentplata.php?month=${checkedMonth}`).then(r => r.json()),
                    fetch(`${API}/get_prixodrasxodmoney.php?month=${checkedMonth}`).then(r => r.json()),
                    fetch(`${API}/get_prixodrasxodgps.php?month=${checkedMonth}`).then(r => r.json()),
                    fetch(`${API}/get_zayavki.php`).then(r => r.json()),
                ]);
                if (r1.status === "success") setAbonentData(r1.data);
                if (r2.status === "success") setMoneyData(r2.data);
                if (r3.status === "success") setGpsData(r3.data);
                if (r4.status === "success") setZayavkiData(r4.data);
            } catch (err) {
                console.error("Reports fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [checkedMonth]);

    // =============================================
    // Вычисляемые итоги — Абонентская плата
    // =============================================
    const abonentStats = () => {
        const active = abonentData.filter(x => !x.deleted && !x.is_new);
        const newOnes = abonentData.filter(x => x.is_new && !x.deleted);
        const deleted = abonentData.filter(x => x.deleted);
        const totalObjects = abonentData.reduce((s, x) => s + (x.deleted ? 0 : Number(x.units_count) || 0), 0);
        const totalCharge = abonentData.reduce((s, x) => {
            if (x.deleted) return s;
            return s + (Number(x.units_count) * Number(x.price_per_unit || 42000));
        }, 0);
        const totalPaid = abonentData.reduce((s, x) => s + (Number(x.paid) || 0), 0);
        // Долг = отрицательный баланс (как на странице абонентской платы)
        const totalDebt = abonentData.reduce((s, x) => {
            const bal = Number(x.balance ?? 0);
            return s + (bal < 0 ? Math.abs(bal) : 0);
        }, 0);
        return { active: active.length, newOnes: newOnes.length, deleted: deleted.length, totalObjects, totalCharge, totalPaid, totalDebt };
    };

    // =============================================
    // Вычисляемые итоги — Деньги
    // =============================================
    const moneyStats = () => {
        const normalizeItem = (item) => ({
            ...item,
            typeMoney: item.typeMoney ?? item.type_money ?? "",
        });
        const list = moneyData.map(normalizeItem);
        const prihod = list.filter(x => x.operation === "Приход").reduce((s, x) => s + Math.abs(Number(x.summa) || 0), 0);
        const rasxod = list.filter(x => x.operation === "Расход").reduce((s, x) => s + Math.abs(Number(x.summa) || 0), 0);
        const ostatok = prihod - rasxod;
        const nal = list.filter(x => x.operation === "Приход" && x.typeMoney === "Наличка").reduce((s, x) => s + Math.abs(Number(x.summa) || 0), 0);
        const per = list.filter(x => x.operation === "Приход" && x.typeMoney === "Перечисление").reduce((s, x) => s + Math.abs(Number(x.summa) || 0), 0);
        return { prihod, rasxod, ostatok, nal, per, count: list.length };
    };

    // =============================================
    // Вычисляемые итоги — GPS
    // =============================================
    const gpsStats = () => {
        const total = gpsData.length;
        const sold = gpsData.filter(x => x.users && x.users.trim() !== "").length;
        const inStock = total - sold;
        const models = {};
        gpsData.forEach(x => {
            if (!models[x.model]) models[x.model] = { total: 0, sold: 0 };
            models[x.model].total++;
            if (x.users && x.users.trim() !== "") models[x.model].sold++;
        });
        return { total, sold, inStock, models };
    };

    // =============================================
    // Вычисляемые итоги — Заявки
    // =============================================
    const zayavkiStats = () => {
        const total = zayavkiData.length;
        const process = zayavkiData.filter(x => x.status == "1").length;
        const success = zayavkiData.filter(x => x.status == "2").length;
        const denied = zayavkiData.filter(x => x.status == "3").length;
        return { total, process, success, denied };
    };

    const fmt = (n) => Number(n || 0).toLocaleString("ru-RU");

    const ab = abonentStats();
    const mn = moneyStats();
    const gp = gpsStats();
    const zv = zayavkiStats();

    // =============================================
    // Печать отчёта
    // =============================================
    const handlePrint = () => window.print();

    return (
        <section className="reports">
            {/* ----- Шапка ----- */}
            <div className="reports-header">
                <h2>📊 Отчёты</h2>
                <button className="reports-print-btn" onClick={handlePrint}>
                    <i className="fas fa-print"></i> Распечатать
                </button>
            </div>

            {/* ----- Выбор месяца ----- */}
            <div className="reports-months">
                {MONTHS.map(([num, label]) => (
                    <span key={num}
                        className={`reports-month-span ${checkedMonth === num ? "active" : ""}`}
                        onClick={() => setCheckedMonth(num)}>
                        {label}
                    </span>
                ))}
            </div>

            {/* ----- Вкладки ----- */}
            <div className="reports-tabs">
                {[
                    ["abonent", "💳 Абонентская плата"],
                    ["money", "💰 Деньги"],
                    ["gps", "📡 GPS Устройства"],
                    ["zayavki", "📋 Заявки"],
                ].map(([key, label]) => (
                    <span key={key}
                        className={`reports-tab ${activeTab === key ? "active" : ""}`}
                        onClick={() => setActiveTab(key)}>
                        {label}
                    </span>
                ))}
            </div>

            {loading ? (
                <div className="reports-loader">
                    <div className="loader"></div>
                    <p>Загрузка данных...</p>
                </div>
            ) : (
                <div className="reports-content" id="reports-print-area">
                    <h3 className="reports-title">
                        {MONTHS.find(([n]) => n === checkedMonth)?.[1]} {new Date().getFullYear()}
                    </h3>

                    {/* ===== АБОНЕНТСКАЯ ПЛАТА ===== */}
                    {activeTab === "abonent" && (
                        <div className="reports-section">
                            <div className="reports-cards">
                                <div className="reports-card reports-card-blue">
                                    <h4>Всего клиентов</h4>
                                    <span>{ab.active + ab.newOnes}</span>
                                </div>
                                <div className="reports-card reports-card-green">
                                    <h4>Новых клиентов</h4>
                                    <span>{ab.newOnes}</span>
                                </div>
                                <div className="reports-card reports-card-red">
                                    <h4>Удалённых</h4>
                                    <span>{ab.deleted}</span>
                                </div>
                                <div className="reports-card reports-card-blue">
                                    <h4>Всего объектов</h4>
                                    <span>{fmt(ab.totalObjects)}</span>
                                </div>
                                <div className="reports-card reports-card-orange">
                                    <h4>К оплате за месяц</h4>
                                    <span>{fmt(ab.totalCharge)} сум</span>
                                </div>
                                <div className="reports-card reports-card-green">
                                    <h4>Оплачено</h4>
                                    <span>{fmt(ab.totalCharge - ab.totalDebt)} сум</span>
                                </div>
                                <div className="reports-card reports-card-red">
                                    <h4>Долг</h4>
                                    <span>{fmt(ab.totalDebt)} сум</span>
                                </div>
                            </div>

                            <h4 className="reports-subtitle">Список клиентов</h4>
                            <table className="reports-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>Клиент</th>
                                        <th>Объектов</th>
                                        <th>Долг</th>
                                        <th>Тип оплаты</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {abonentData.map((x, i) => {
                                        const charge = Number(x.units_count) * Number(x.price_per_unit || 42000);
                                        const debt = Number(x.debt) || 0;
                                        return (
                                            <tr key={x.id}
                                                style={{ backgroundColor: x.deleted ? "#ffe5e5" : x.is_new ? "#e5f0ff" : "" }}>
                                                <td>{i + 1}</td>
                                                <td>{x.account_name}</td>
                                                <td>{x.units_count}</td>
                                                <td style={{ color: debt > 0 ? "red" : "green" }}>{fmt(debt)}</td>
                                                <td>{x.abonentplata_method === "nal" ? "Наличка" : x.abonentplata_method === "per" ? "Перечисление" : "—"}</td>
                                                <td>{x.deleted ? "❌ Удалён" : x.is_new ? "🆕 Новый" : "✅ Активный"}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ===== ДЕНЬГИ ===== */}
                    {activeTab === "money" && (
                        <div className="reports-section">
                            <div className="reports-cards">
                                <div className="reports-card reports-card-green">
                                    <h4>Приход</h4>
                                    <span>{fmt(mn.prihod)} сум</span>
                                </div>
                                <div className="reports-card reports-card-red">
                                    <h4>Расход</h4>
                                    <span>{fmt(mn.rasxod)} сум</span>
                                </div>
                                <div className="reports-card reports-card-blue">
                                    <h4>Остаток</h4>
                                    <span style={{ color: mn.ostatok < 0 ? "red" : "inherit" }}>{fmt(mn.ostatok)} сум</span>
                                </div>
                                <div className="reports-card reports-card-orange">
                                    <h4>Наличка (приход)</h4>
                                    <span>{fmt(mn.nal)} сум</span>
                                </div>
                                <div className="reports-card reports-card-blue">
                                    <h4>Перечисление (приход)</h4>
                                    <span>{fmt(mn.per)} сум</span>
                                </div>
                                <div className="reports-card reports-card-blue">
                                    <h4>Всего операций</h4>
                                    <span>{mn.count}</span>
                                </div>
                            </div>

                            <h4 className="reports-subtitle">Движение средств</h4>
                            <table className="reports-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>Дата</th>
                                        <th>Тип оплаты</th>
                                        <th>Операция</th>
                                        <th>Источник</th>
                                        <th>Наименование</th>
                                        <th>Сумма</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {moneyData.map((x, i) => (
                                        <tr key={x.id}
                                            style={{ color: x.operation === "Расход" ? "red" : "green" }}>
                                            <td>{i + 1}</td>
                                            <td>{x.date}</td>
                                            <td>{x.type_money ?? x.typeMoney}</td>
                                            <td>{x.operation}</td>
                                            <td>{x.source}</td>
                                            <td>{x.name}</td>
                                            <td>{fmt(Math.abs(Number(x.summa)))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ===== GPS ===== */}
                    {activeTab === "gps" && (
                        <div className="reports-section">
                            <div className="reports-cards">
                                <div className="reports-card reports-card-blue">
                                    <h4>Всего оприходовано</h4>
                                    <span>{gp.total}</span>
                                </div>
                                <div className="reports-card reports-card-green">
                                    <h4>Продано / Установлено</h4>
                                    <span>{gp.sold}</span>
                                </div>
                                <div className="reports-card reports-card-orange">
                                    <h4>В наличии</h4>
                                    <span>{gp.inStock}</span>
                                </div>
                            </div>

                            <h4 className="reports-subtitle">По моделям</h4>
                            <div className="reports-cards">
                                {Object.entries(gp.models).map(([model, stat]) => (
                                    <div key={model} className="reports-card reports-card-blue">
                                        <h4>{model}</h4>
                                        <span>В наличии: {stat.total - stat.sold}</span>
                                        <span>Всего: {stat.total}</span>
                                    </div>
                                ))}
                            </div>

                            <h4 className="reports-subtitle">Список GPS устройств</h4>
                            <table className="reports-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>Дата</th>
                                        <th>Модель</th>
                                        <th>IMEI</th>
                                        <th>S/N</th>
                                        <th>Учетная запись</th>
                                        <th>Транспорт</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gpsData.map((x, i) => (
                                        <tr key={x.id}
                                            style={{ backgroundColor: !x.users || x.users.trim() === "" ? "#ffe5e5" : "#96ebc556" }}>
                                            <td>{i + 1}</td>
                                            <td>{x.date}</td>
                                            <td>{x.model}</td>
                                            <td>{x.imei}</td>
                                            <td>{x.sn}</td>
                                            <td>{x.users || "—"}</td>
                                            <td>{x.unit || "—"}</td>
                                            <td>{x.users && x.users.trim() ? "✅ Продан" : "📦 В наличии"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ===== ЗАЯВКИ ===== */}
                    {activeTab === "zayavki" && (
                        <div className="reports-section">
                            <div className="reports-cards">
                                <div className="reports-card reports-card-blue">
                                    <h4>Всего заявок</h4>
                                    <span>{zv.total}</span>
                                </div>
                                <div className="reports-card reports-card-orange">
                                    <h4>В процессе</h4>
                                    <span>{zv.process}</span>
                                </div>
                                <div className="reports-card reports-card-green">
                                    <h4>Успешно</h4>
                                    <span>{zv.success}</span>
                                </div>
                                <div className="reports-card reports-card-red">
                                    <h4>Отказано</h4>
                                    <span>{zv.denied}</span>
                                </div>
                            </div>

                            <h4 className="reports-subtitle">Список заявок</h4>
                            <table className="reports-table">
                                <thead>
                                    <tr>
                                        <th>№</th>
                                        <th>Имя</th>
                                        <th>Дата</th>
                                        <th>Местоположение</th>
                                        <th>Телефон</th>
                                        <th>Тип</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {zayavkiData.map((x, i) => (
                                        <tr key={x.id}
                                            style={{
                                                color: x.status == 3 ? "red" : x.status == 2 ? "green" : "",
                                                backgroundColor: x.status == 3 ? "#ffe5e5" : x.status == 2 ? "#96ebc556" : ""
                                            }}>
                                            <td>{i + 1}</td>
                                            <td>{x.name_client ?? x.nameClient}</td>
                                            <td>{x.date}</td>
                                            <td>{x.location_client ?? x.locationClient}</td>
                                            <td>{x.phone_number_client ?? x.phoneNumberClient}</td>
                                            <td>{x.type_request ?? x.typeRequest}</td>
                                            <td>{x.status == 1 ? "⏳ В процессе" : x.status == 2 ? "✅ Успешно" : "❌ Отказано"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default Reports;
